import { Check } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  ChipToggle,
  Dialog,
  Field,
  Input,
} from '@/components/ui';
import type { VitalsInput } from '@/features/queue/types';
import { bmi, liveFlags } from '@/features/queue/vital-limits';
import { AllergyList } from '@/pages/patients/AllergyList';
import { ageOf, type VitalsTarget } from './queue-format';
import styles from './queue.module.css';

type FieldKey = Exclude<keyof VitalsInput, 'glucoseContext'>;
const FIELDS: { key: FieldKey; label: string; step?: string }[] = [
  { key: 'bpSystolic', label: 'BP systolic' },
  { key: 'bpDiastolic', label: 'BP diastolic' },
  { key: 'pulse', label: 'Pulse /min' },
  { key: 'temperatureC', label: 'Temperature °C', step: '0.1' },
  { key: 'spo2', label: 'SpO2 %' },
  { key: 'pain', label: 'Pain (0 to 10)' },
  { key: 'weightKg', label: 'Weight kg', step: '0.1' },
  { key: 'heightCm', label: 'Height cm', step: '0.1' },
  { key: 'glucoseMmol', label: 'Glucose mmol/L', step: '0.1' },
];

/** "Vitals at triage": values outside the normal range are flagged as they are typed. */
export function VitalsDrawer({
  ticket,
  onClose,
  onSave,
  busy,
}: {
  ticket: VitalsTarget | null;
  onClose: () => void;
  onSave: (v: VitalsInput) => void;
  busy?: boolean;
}) {
  const [raw, setRaw] = useState<Record<FieldKey, string>>(
    {} as Record<FieldKey, string>,
  );
  const [context, setContext] = useState<'random' | 'fasting'>('random');
  const values: VitalsInput = Object.fromEntries(
    FIELDS.filter((f) => raw[f.key]?.trim()).map((f) => [
      f.key,
      Number(raw[f.key]),
    ]),
  );
  if (values.glucoseMmol !== undefined) values.glucoseContext = context;
  const flags = liveFlags(values);
  const b = bmi(values.weightKg, values.heightCm);
  const empty =
    Object.keys(values).filter((k) => k !== 'glucoseContext').length === 0;

  return (
    <Dialog
      open={!!ticket}
      onClose={onClose}
      dismissible={false}
      title={`Vitals · ${ticket?.patientName ?? ''}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={<Check size={16} />}
            disabled={empty}
            loading={busy}
            onClick={() => onSave(values)}
          >
            Save vitals
          </Button>
        </>
      }
    >
      {ticket && (
        <div className={styles.drawer}>
          <dl className={styles.rows}>
            <dt>Queue</dt>
            <dd>
              {ticket.number} · {ticket.service ?? 'Walk-in'}
            </dd>
            <dt>Age</dt>
            <dd>{ageOf(ticket.dateOfBirth) ?? '—'}</dd>
            <dt>Allergies</dt>
            <dd>
              <AllergyList allergies={ticket.allergies} />
            </dd>
          </dl>
          <div className={styles.vitalsGrid}>
            {FIELDS.map((f) => (
              <Field key={f.key} label={f.label}>
                <Input
                  type="number"
                  inputMode="decimal"
                  step={f.step ?? '1'}
                  value={raw[f.key] ?? ''}
                  onChange={(e) => setRaw({ ...raw, [f.key]: e.target.value })}
                />
              </Field>
            ))}
          </div>
          <div className={styles.section}>
            <p className="eyebrow">Glucose taken</p>
            <ChipToggle
              label="Glucose taken"
              single
              options={[
                { value: 'random', label: 'Random' },
                { value: 'fasting', label: 'Fasting' },
              ]}
              selected={[context]}
              onChange={([c]) => setContext(c)}
            />
          </div>
          <p className={styles.flagLine} aria-live="polite">
            {flags.length ? (
              flags.map((f) => (
                <Badge key={f} tone="danger">
                  {f}
                </Badge>
              ))
            ) : (
              <span className={styles.sub}>Flags appear here as you type</span>
            )}
            {b !== null && <span> BMI {b}</span>}
          </p>
        </div>
      )}
    </Dialog>
  );
}
